import React, { useMemo, useRef } from 'react';
import { Box, Typography, ButtonBase, Avatar, Chip } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import DateRangeFilter, {
  DateRangeChip,
  isRangeFiltered,
} from '../shared/DateRangeFilter';
import { TOKENS, BRAND, avatarColors } from '../theme';
import { parseUsDateString } from './EmployeeSessionsScreen';

const DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// clockIn/clockOut arrive as 'MM/dd/yyyy h:mm a' strings already in the
// location's (Chicago) time, so grouping on the parsed calendar date files
// each shift under the day it STARTED — an overnight shift stays on the
// clock-in day, same rule as deriveAssignedDay.
function parseTime(raw) {
  if (!raw) return null;
  const us = parseUsDateString(raw);
  if (us) return us;
  const d = new Date(raw?.$date || raw);
  return isNaN(d.getTime()) ? null : d;
}

function dayKey(d) {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function formatTime(d) {
  let h = d.getHours();
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${String(d.getMinutes()).padStart(2, '0')} ${ap}`;
}

function formatShortDate(d) {
  const label = `${MONTHS[d.getMonth()]} ${d.getDate()}`;
  return d.getFullYear() === new Date().getFullYear()
    ? label
    : `${label}, ${d.getFullYear()}`;
}

// Monday of the week `d` falls in (weeks run Monday → Sunday).
function mondayOf(d) {
  return new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate() - ((d.getDay() + 6) % 7)
  );
}

// "By Day" tab of the Employee Sessions root. Days are grouped into weeks
// (newest week first) and listed Monday → Sunday inside each week; only days
// that have at least one shift get a row.
// Tapping a day expands it to list every shift that started that day with
// its clock-in → clock-out times. Tapping a shift opens its session reports,
// same as the By Employee path.
export default function SessionsByDayScreen({
  sessions,
  expandedDay,
  onToggleDay,
  onSelectSession,
  fromDate,
  toDate,
  onDateChange,
  onDateReset,
  colorOf,
}) {
  const openFilterRef = useRef(null);
  const weeks = useMemo(() => {
    const map = new Map();
    const addDay = (d) => {
      const key = dayKey(d);
      if (!map.has(key)) {
        const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        map.set(key, { key, date, shifts: [] });
      }
      return map.get(key);
    };
    (sessions || []).forEach((s) => {
      const start = parseTime(s.clockIn || s.timeIn);
      if (!start) return;
      const end = parseTime(s.clockOut || s.timeOut);
      addDay(start).shifts.push({ session: s, start, end });
    });
    const byWeek = new Map();
    Array.from(map.values())
      .sort((a, b) => (a.key < b.key ? -1 : 1))
      .forEach((day) => {
        const monday = mondayOf(day.date);
        const wk = dayKey(monday);
        if (!byWeek.has(wk)) byWeek.set(wk, { key: wk, monday, days: [] });
        byWeek.get(wk).days.push({
          ...day,
          shifts: day.shifts.sort((a, b) => a.start - b.start),
          employeeCount: new Set(day.shifts.map((x) => x.session.name)).size,
        });
      });
    return Array.from(byWeek.values()).sort((a, b) => (a.key < b.key ? 1 : -1));
  }, [sessions]);

  const rangeLabel =
    fromDate && toDate
      ? `${formatShortDate(fromDate)} – ${formatShortDate(toDate)}`
      : '';

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{ fontSize: 26, fontWeight: 700, color: TOKENS.slate900 }}
        >
          Sessions by Day
        </Typography>
        <Typography sx={{ fontSize: 15, color: TOKENS.slate500, mt: 0.25 }}>
          Tap a day to see who worked on that day
        </Typography>
      </Box>

      {onDateChange && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <DateRangeFilter
            fromDate={fromDate}
            toDate={toDate}
            onDateChange={onDateChange}
            openRef={openFilterRef}
          />
          {/* Default 30 days: the plain range text, as before. A filtered
              range: the chip instead (click reopens, × resets). */}
          {isRangeFiltered(fromDate, toDate) ? (
            <DateRangeChip
              fromDate={fromDate}
              toDate={toDate}
              onClick={() => openFilterRef.current && openFilterRef.current()}
              onReset={onDateReset}
            />
          ) : (
            <Typography sx={{ fontSize: 14, color: TOKENS.slate500 }}>
              {rangeLabel}
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {weeks.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              color: TOKENS.slate400,
              fontSize: 13,
              background: '#fff',
              border: `1px solid ${TOKENS.slate200}`,
              borderRadius: 2,
            }}
          >
            No sessions in this date range.
          </Box>
        )}
        {weeks.map((week) => {
          const sunday = new Date(
            week.monday.getFullYear(),
            week.monday.getMonth(),
            week.monday.getDate() + 6
          );
          return (
            <React.Fragment key={week.key}>
              <Typography
                sx={{
                  pt: 1,
                  fontSize: 13,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: TOKENS.slate500,
                }}
              >
                Week of {formatShortDate(week.monday)} –{' '}
                {formatShortDate(sunday)}
              </Typography>
              {week.days.map((day) => {
                const open = expandedDay === day.key;
                return (
                  <Box
                    key={day.key}
                    sx={{
                      background: '#fff',
                      border: `1px solid ${
                        open ? BRAND.navy : TOKENS.slate200
                      }`,
                      // Selected: a 2px navy edge, the same navy as the
                      // active tab. The extra 1px is a ring (box-shadow) so
                      // the card doesn't grow and shift the list.
                      boxShadow: open ? `0 0 0 1px ${BRAND.navy}` : 'none',
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}
                  >
                    <ButtonBase
                      onClick={() => onToggleDay(open ? null : day.key)}
                      aria-expanded={open}
                      sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        px: 2,
                        py: 1.25,
                        textAlign: 'left',
                        justifyContent: 'flex-start',
                        background: open ? BRAND.track : 'transparent',
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: 16,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.02em',
                            color: open ? BRAND.navy : TOKENS.slate900,
                          }}
                        >
                          {DAYS[day.date.getDay()]}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: open ? BRAND.ink : TOKENS.slate500,
                          }}
                        >
                          {formatShortDate(day.date)}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 14,
                          color: open ? BRAND.ink : TOKENS.slate500,
                          mr: 1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {day.employeeCount}{' '}
                        {day.employeeCount === 1 ? 'employee' : 'employees'}
                      </Typography>
                      {open ? (
                        <ExpandMoreIcon sx={{ color: BRAND.ink }} />
                      ) : (
                        <ChevronRightIcon sx={{ color: TOKENS.slate400 }} />
                      )}
                    </ButtonBase>

                    {open &&
                      day.shifts.map(({ session: s, start, end }) => {
                        return (
                          <ButtonBase
                            key={s.clockId}
                            onClick={() => onSelectSession(s)}
                            sx={{
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              px: 2,
                              py: 1.25,
                              borderTop: `1px solid ${TOKENS.slate100}`,
                              textAlign: 'left',
                              justifyContent: 'flex-start',
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                ...avatarColors(s.userId, s.name, colorOf),
                                fontWeight: 700,
                                fontSize: 14,
                              }}
                            >
                              {(s.name || '?').charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography
                              noWrap
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                fontSize: 15,
                                fontWeight: 700,
                                color: TOKENS.slate900,
                              }}
                            >
                              {s.name || '—'}
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                flexShrink: 0,
                                fontSize: 14,
                                color: TOKENS.slate600,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <span>{formatTime(start)}</span>
                              {end ? (
                                <React.Fragment>
                                  <span style={{ color: TOKENS.slate400 }}>
                                    →
                                  </span>
                                  <span>{formatTime(end)}</span>
                                </React.Fragment>
                              ) : (
                                <Chip
                                  size="small"
                                  label="In progress"
                                  sx={{
                                    height: 22,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    background: TOKENS.emerald50,
                                    color: TOKENS.emerald700,
                                    border: `1px solid ${TOKENS.emerald200}`,
                                  }}
                                />
                              )}
                            </Box>
                          </ButtonBase>
                        );
                      })}
                  </Box>
                );
              })}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}
